import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Lock, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { isTopicLocked, isUserSubscribed, FREE_TOPICS } from '../../services/subscriptionGate';
import { testCountTracker } from '../../services/testCountTracker';
import { aiCoach } from '../../services/aiCoach';
import { logger } from '../../utils/logger';
import { prefetchAllTestRoutes } from '../../utils/smartPrefetch';
import { advancedCache } from '../../services/advancedCache';
import { memoizedRecommendations } from '../../services/memoizedRecommendations';
import { userAuth } from '../../services/userAuth';
import { getExamUnlockState } from '../../services/aiExamUnlockService';
import { formatTestNameForDisplay, getTranslation } from '../../utils/translationHelpers';
import { SettingsDropdown } from '../SettingsDropdown';
import styles from './PracticePage.module.css';

interface Topic {
  id: string;
  name: string;
  questionCount: number;
}

const ALL_TOPIC_IDS = [
  'road-markings', 'motorway-rules', 'traffic-signs', 'vehicle-handling',
  'tram-interactions', 'risk-awareness', 'night-visibility', 'adas-safety',
  'weather-conditions', 'incident-first-aid', 'pedestrian-crossings',
  'vehicle-categories', 'construction-zones', 'technology-safety',
  'fatigue-rest', 'environmental-zones', 'vehicle-documentation',
  'priority-rules', 'mandatory-signs', 'traffic-lights-signals',
  'prohibitory-signs', 'roundabout-rules', 'emergency-procedures',
  'alcohol-drugs', 'specialized-driving',
];

export const PracticePage: React.FC = () => {
  const navigate = useNavigate();
  const { t_nested, currentLanguage } = useLanguage();

  const getTranslatedTestName = (testId: string): string => {
    const map: Record<string, string> = {
      'road-markings': t_nested('testNames.roadMarkings'),
      'motorway-rules': t_nested('testNames.motorwayRules'),
      'traffic-signs': t_nested('testNames.trafficSigns'),
      'vehicle-handling': t_nested('testNames.vehicleHandling'),
      'tram-interactions': t_nested('testNames.tramInteractions'),
      'risk-awareness': t_nested('testNames.riskAwareness'),
      'night-visibility': t_nested('testNames.nightVisibility'),
      'weather-conditions': t_nested('testNames.weatherConditions'),
      'incident-first-aid': t_nested('testNames.incidentFirstAid'),
      'pedestrian-crossings': t_nested('testNames.pedestrianCrossings'),
      'vehicle-categories': t_nested('testNames.vehicleCategories'),
      'construction-zones': t_nested('testNames.constructionZones'),
      'technology-safety': t_nested('testNames.technologySafety'),
      'fatigue-rest': t_nested('testNames.fatigueRest'),
      'environmental-zones': t_nested('testNames.environmentalZones'),
      'vehicle-documentation': t_nested('testNames.vehicleDocumentation'),
      'priority-rules': t_nested('testNames.priorityRules'),
      'mandatory-signs': t_nested('testNames.mandatorySigns'),
      'traffic-lights-signals': t_nested('testNames.trafficLightsSignals'),
      'prohibitory-signs': t_nested('testNames.prohibitorySigns'),
      'roundabout-rules': t_nested('testNames.roundaboutRules'),
      'emergency-procedures': t_nested('testNames.emergencyProcedures'),
      'alcohol-drugs': t_nested('testNames.alcoholDrugs'),
      'specialized-driving': getTranslation(t_nested, 'testNames.specializedDriving', 'Specialized driving'),
      'adas-safety': t_nested('testNames.adasSafety'),
    };
    const translated = map[testId];
    if (!translated || translated === testId || translated.startsWith('testNames.')) {
      return testId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
    return formatTestNameForDisplay(translated);
  };

  const buildTopics = (): Topic[] => [
    { id: 'road-markings', name: getTranslatedTestName('road-markings'), questionCount: 40 },
    { id: 'motorway-rules', name: getTranslatedTestName('motorway-rules'), questionCount: 60 },
    { id: 'traffic-signs', name: getTranslatedTestName('traffic-signs'), questionCount: 88 },
    { id: 'vehicle-handling', name: getTranslatedTestName('vehicle-handling'), questionCount: 80 },
    { id: 'tram-interactions', name: getTranslatedTestName('tram-interactions'), questionCount: 88 },
    { id: 'risk-awareness', name: getTranslatedTestName('risk-awareness'), questionCount: 80 },
    { id: 'night-visibility', name: getTranslatedTestName('night-visibility'), questionCount: 80 },
    { id: 'adas-safety', name: getTranslatedTestName('adas-safety'), questionCount: 100 },
    { id: 'weather-conditions', name: getTranslatedTestName('weather-conditions'), questionCount: 40 },
    { id: 'incident-first-aid', name: getTranslatedTestName('incident-first-aid'), questionCount: 80 },
    { id: 'pedestrian-crossings', name: getTranslatedTestName('pedestrian-crossings'), questionCount: 40 },
    { id: 'vehicle-categories', name: getTranslatedTestName('vehicle-categories'), questionCount: 60 },
    { id: 'construction-zones', name: getTranslatedTestName('construction-zones'), questionCount: 40 },
    { id: 'technology-safety', name: getTranslatedTestName('technology-safety'), questionCount: 60 },
    { id: 'fatigue-rest', name: getTranslatedTestName('fatigue-rest'), questionCount: 48 },
    { id: 'environmental-zones', name: getTranslatedTestName('environmental-zones'), questionCount: 46 },
    { id: 'vehicle-documentation', name: getTranslatedTestName('vehicle-documentation'), questionCount: 48 },
    { id: 'priority-rules', name: getTranslatedTestName('priority-rules'), questionCount: 20 },
    { id: 'mandatory-signs', name: getTranslatedTestName('mandatory-signs'), questionCount: 20 },
    { id: 'traffic-lights-signals', name: getTranslatedTestName('traffic-lights-signals'), questionCount: 20 },
    { id: 'prohibitory-signs', name: getTranslatedTestName('prohibitory-signs'), questionCount: 20 },
    { id: 'roundabout-rules', name: getTranslatedTestName('roundabout-rules'), questionCount: 22 },
    { id: 'emergency-procedures', name: getTranslatedTestName('emergency-procedures'), questionCount: 20 },
    { id: 'alcohol-drugs', name: getTranslatedTestName('alcohol-drugs'), questionCount: 20 },
    { id: 'specialized-driving', name: getTranslatedTestName('specialized-driving'), questionCount: 20 },
  ];

  const [recommendedTest, setRecommendedTest] = useState<{ id: string; name: string } | null>(null);
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(true);
  const [testScores, setTestScores] = useState<Record<string, number>>(() => {
    const scores: Record<string, number> = {};
    ALL_TOPIC_IDS.forEach(id => {
      const score = aiCoach.getTestScore(id);
      if (score !== null && score !== undefined) scores[id] = score;
    });
    return scores;
  });

  const topics = buildTopics();

  const refreshScores = React.useCallback(() => {
    const scores: Record<string, number> = {};
    ALL_TOPIC_IDS.forEach(id => {
      const score = aiCoach.getTestScore(id);
      if (score !== null && score !== undefined) scores[id] = score;
    });
    setTestScores(scores);
  }, []);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) refreshScores(); };
    const onFocus = () => refreshScores();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('testCompleted', refreshScores);
    window.addEventListener('practiceTestCompleted', refreshScores);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('testCompleted', refreshScores);
      window.removeEventListener('practiceTestCompleted', refreshScores);
    };
  }, [refreshScores]);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      try {
        const currentUser = userAuth.getCurrentUser();
        const userId = currentUser?.id || 'guest';
        const examState = getExamUnlockState();

        if (examState.activeExamInstanceId) {
          if (mounted) {
            setRecommendedTest({
              id: 'ai-exam',
              name: getTranslation(t_nested, 'navigation.aiExam', 'Personalised exam'),
            });
            setIsLoadingRecommendation(false);
          }
          return;
        }

        const subscribed = isUserSubscribed();
        if (!subscribed) {
          const completed = testCountTracker.getTestCount();
          const freeAiExamPending = testCountTracker.getFreeAiExamPending();
          if (!mounted) return;
          if (completed < 3) {
            const nextId = FREE_TOPICS[completed] || FREE_TOPICS[0];
            setRecommendedTest({ id: nextId, name: formatTestNameForDisplay(getTranslatedTestName(nextId)) });
          } else if (freeAiExamPending) {
            setRecommendedTest({
              id: 'ai-exam',
              name: getTranslation(t_nested, 'navigation.aiExam', 'Personalised exam'),
            });
          } else {
            const fallback = FREE_TOPICS[0];
            setRecommendedTest({ id: fallback, name: formatTestNameForDisplay(getTranslatedTestName(fallback)) });
          }
          setIsLoadingRecommendation(false);
          return;
        }

        const rec = memoizedRecommendations.getRecommendation(userId, t_nested);
        if (!mounted) return;
        if (rec?.testId) {
          setRecommendedTest({ id: rec.testId, name: formatTestNameForDisplay(getTranslatedTestName(rec.testId)) });
        } else {
          setRecommendedTest(null);
        }
      } catch (err) {
        logger.error('Error loading recommendation:', err);
        if (mounted) setRecommendedTest(null);
      } finally {
        if (mounted) setIsLoadingRecommendation(false);
      }
    };
    load();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  useEffect(() => {
    prefetchAllTestRoutes(ALL_TOPIC_IDS);
  }, []);

  useEffect(() => {
    const lang = currentLanguage || 'nl';
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        advancedCache.preloadQuestions(ALL_TOPIC_IDS, lang).catch(err => {
          logger.error('Error preloading questions:', err);
        });
      });
    } else {
      setTimeout(() => {
        advancedCache.preloadQuestions(ALL_TOPIC_IDS, lang).catch(err => {
          logger.error('Error preloading questions:', err);
        });
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  const handleTopicClick = (topicId: string) => {
    if (isTopicLocked(topicId)) {
      navigate('/subscription');
      return;
    }
    window.location.href = `/practice/${topicId}`;
  };

  const handleStartRecommended = () => {
    if (!recommendedTest) return;
    if (recommendedTest.id !== 'ai-exam' && isTopicLocked(recommendedTest.id)) {
      navigate('/subscription');
      return;
    }
    const examState = getExamUnlockState();
    const href = recommendedTest.id === 'ai-exam'
      ? (examState.activeExamInstanceId
          ? `/ai-exam?instanceId=${encodeURIComponent(examState.activeExamInstanceId)}&skipIntro=true`
          : '/ai-exam')
      : `/practice/${recommendedTest.id}`;
    window.location.href = href;
  };

  const completedCount = ALL_TOPIC_IDS.filter(id => (testScores[id] ?? 0) >= 100).length;
  const featuredScore = recommendedTest ? (testScores[recommendedTest.id] ?? 0) : 0;
  const featuredTopic = recommendedTest
    ? topics.find(t => t.id === recommendedTest.id)
    : null;

  const backLabel = getTranslation(t_nested, 'aria.backToDashboard', 'Back to dashboard');

  const recommendationLoadingAria = getTranslation(
    t_nested,
    'loading.recommendationSkeleton',
    'Loading recommendation'
  );

  return (
    <div className={`${styles.page} practice-main-page`}>
      <div className={styles.topBar}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate('/dashboard')}
          aria-label={backLabel}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className={styles.topBarHeadings}>
          <h1 className={styles.pageTitle}>
            {getTranslation(t_nested, 'practice.title', 'Practice')}
          </h1>
        </div>
        <div className={styles.topBarSettings}>
          <SettingsDropdown />
        </div>
      </div>

      {/* Featured card */}
      <div className={styles.featuredSection}>
        {isLoadingRecommendation ? (
          <div
            className={styles.skeletonCard}
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label={recommendationLoadingAria}
          >
            <div className={`${styles.skeletonLine}`} style={{ width: '45%' }} />
            <div className={`${styles.skeletonLine}`} style={{ width: '70%', height: '22px' }} />
            <div className={`${styles.skeletonLine}`} style={{ width: '60%' }} />
          </div>
        ) : recommendedTest ? (
          <div className={styles.featuredCard}>
            <div className={styles.featuredBadge}>
              <span className={styles.featuredBadgeDot} />
              {getTranslation(t_nested, 'practice.recommendedForYou', 'Recommended for you')}
            </div>
            <h2 className={styles.featuredTitle}>{recommendedTest.name}</h2>
            {featuredTopic && (
              <div className={styles.progressContainer}>
                <div className={styles.progressLabel}>
                  <span className={styles.progressPercent}>{featuredScore}%</span>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${featuredScore}%` }}
                    role="progressbar"
                    aria-valuenow={featuredScore}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            )}
            <button
              type="button"
              className={styles.featuredStartCta}
              onClick={handleStartRecommended}
            >
              <Play size={16} aria-hidden="true" />
              {getTranslation(t_nested, 'practice.startPractice', 'Start practice')}
            </button>
          </div>
        ) : null}
      </div>

      {/* All topics */}
      <div className={styles.allTopicsSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            {getTranslation(t_nested, 'practice.allPracticeTests', 'All practice tests')}
          </h2>
          <span className={styles.sectionCount}>
            {completedCount} / {ALL_TOPIC_IDS.length}
          </span>
        </div>
        <div className={styles.topicGrid}>
          {topics.map(topic => {
            const locked = isTopicLocked(topic.id);
            const score = testScores[topic.id] ?? 0;
            const completed = score >= 100;
            const topicAriaSuffix = locked
              ? getTranslation(t_nested, 'practice.topicCardAriaLocked', ', locked')
              : getTranslation(t_nested, 'practice.topicCardAriaProgress', ', {{score}}% complete').replace(
                  '{{score}}',
                  String(score)
                );
            const topicTitleLang =
              currentLanguage === 'ar' ? 'ar' : currentLanguage === 'en' ? 'en' : 'nl';
            return (
              <button
                key={topic.id}
                type="button"
                className={[
                  styles.topicCard,
                  completed ? styles.completedCard : '',
                  locked ? styles.lockedCard : '',
                ].join(' ')}
                onClick={() => !locked && handleTopicClick(topic.id)}
                aria-label={`${topic.name}${topicAriaSuffix}`}
                aria-disabled={locked}
              >
                <div className={styles.topicCardTop}>
                  <span className={styles.topicTitle} lang={topicTitleLang}>{topic.name}</span>
                  {completed && (
                    <CheckCircle2
                      size={14}
                      className={styles.checkIcon}
                      aria-hidden="true"
                    />
                  )}
                  {locked && !completed && (
                    <Lock
                      size={14}
                      className={styles.lockIcon}
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div className={styles.topicProgress}>
                  <div className={styles.topicProgressBar}>
                    <div
                      className={`${styles.topicProgressFill}${completed ? ` ${styles.completedFill}` : ''}`}
                      style={{ width: `${score}%` }}
                    />
                  </div>
                  <span className={`${styles.topicProgressText}${completed ? ` ${styles.completedText}` : ''}`}>
                    {locked ? '—' : `${score}%`}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
